import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton, quantityInput } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const isLocked = quantityInput.disabled || this.closest('.cart-items-disabled');

    // In cart, quantity 1 can still be decreased because 1 - 1 removes the line item.
    minusButton.disabled = Boolean(isLocked) || value <= 0;
    plusButton.disabled = Boolean(isLocked) || value <= 0 || (effectiveMax !== null && value >= effectiveMax);
  }

  /**
   * Handles the quantity decrease event in cart context.
   * Cart semantics allow the final decrement to become 0, which removes the line item.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof Element)) return;
    event.preventDefault();

    const { quantityInput, minusButton, plusButton } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const newValue = value - step < min ? 0 : value - step;

    quantityInput.value = newValue.toString();

    if (newValue === 0) {
      minusButton.disabled = true;
      plusButton.disabled = true;
    }

    this.onQuantityChange();
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
